import * as React from 'react'
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router'
import { BrowserRouter as Router } from 'react-router-dom'
import { guestDashboardPath, guestOrganizationsPath, guestPortfolioPath, guestProfilePath, guestWalletsPath, organizationListPath, Paths, profilePath, dashboardPath, User } from '@logic'
import { StateTransform } from '@src/index'
import { connect } from 'react-redux'
import { StyledRoutes, StyledRoutesContainer } from '@src/app/components/routes/style'
import { Banner } from '@components/banner/banner'
import { userIsEmailVerified, userIsLoggedIn } from '@helpers/util'
import { Loading } from '@components/loading'
import { ChunkErrorBoundary } from './chunk-error-boundary'
import { aiRoutes, equabotzRoutes, fleetTrackerRoutes, guestOrganizationsRoutes, guestRoutes, legacyRedirectRoutes, marketingRoutes, organizationListRoutes, organizationRoutes, profileRoutes, routes, taskThreadRoutes } from '@src/app/routes'
import {
  aiRootPath,
  aiSettingsPath,
  organizationEquabotzAliasPath,
  organizationEquabotzPath,
  equabotSettingsPath,
  equabotzPath,
  equabotzSettingsPath,
  fleetTrackerPath,
  primePath,
  aboutPath,
  featuresPath,
  pricingPath,
  contactPath,
  privacyPath,
  termsPath,
  blogPath,
  blogPostPath,
  caseStudiesPath,
  integrationsPath,
  securityPath,
  changelogPath,
} from '@logic'
import { SubHeader, SubTitle } from '@src/app/components/header/sub-header'
import { PageTitles } from '@shared/components/page-titles'
import { matchesPath, withNavigation } from '@shared/components/navigation'
import { Header } from '../header/header'
import { ComponentType } from 'react'
import { withTracker } from '@shared/hocs/with-tracker'
import { withService } from '@shared/hocs/with-service'
import { buildEquabotzSettingsSearch } from '@modules/equabot-settings/services/scope-query'

interface Props {
  initialLoad: boolean
}

interface RoutesProps {
  user?: User
}

export interface RouteDefinition {
  path: string
  protected?: boolean
  redirect?: string
  component?: any
  requires?: any
  routes?: RouteDefinition[]
}

function equabotzSettingsRedirect(location: RouteComponentProps['location'], orgId?: string) {
  return {
    pathname: equabotzSettingsPath,
    search: buildEquabotzSettingsSearch(location.search, orgId),
    hash: location.hash,
  }
}

function getRouteRenderWithAuth(user: User | undefined, isLoggedIn: boolean, route: RouteDefinition, i: number) {
  if (isLoggedIn === route.protected || !route.redirect) {
    return () => {
      const RouteComponent = route.requires ? route.requires(route.component) : route.component
      return (
        <>
          {PageTitles()}
          <RouteComponent user={user} />
        </>
      )

    }
  } else {
    return (routeProps: RouteComponentProps<any>) => {
      const from = route.redirect == Paths.userLogin ? `?from=${routeProps.match.url}` : ''
      return <Redirect to={`${route.redirect!}${from}`} />
    }
  }
}

const Routes = (props: RoutesProps & Props) => {
  const { user } = props
  const isLoggedIn = userIsLoggedIn(user) && userIsEmailVerified(user)
  if (!props.initialLoad) {
    return <Loading />
  }

  const mapRoutes = (route: RouteDefinition, i: number) => {
    const render = getRouteRenderWithAuth(user, isLoggedIn, route, i)
    const rest = { render }
    return <Route key={i} path={route.path} exact {...rest} />
  }

  return (
    <StyledRoutesContainer user={user ? 'true' : 'false'}>
      <Banner />
      <StyledRoutes>
        <ChunkErrorBoundary>
          <Switch>
            {/* FIX-QA059-02: /profile alone renders a black screen because the inner Switch
                finds no exact match. Redirect to /profile/dashboard (the real dashboard). */}
            <Route exact path={profilePath}>
              <Redirect to={isLoggedIn ? dashboardPath : Paths.userLogin} />
            </Route>

            {/* Legacy redirects: /service → /admin, /command-center → /prime, /profile/equabot → /equabotz/settings, /ai/settings → /equabotz/settings */}
            {legacyRedirectRoutes.map((r, i) => (
              <Route
                key={`redirect-${i}`}
                exact
                path={r.path}
                render={({ location }) => (
                  <Redirect to={{ pathname: r.redirect!, search: location.search, hash: location.hash }} />
                )}
              />
            ))}
            <Route exact path={equabotSettingsPath} render={({ location }) => <Redirect to={equabotzSettingsRedirect(location)} />} />
            <Route exact path={aiSettingsPath} render={({ location }) => <Redirect to={equabotzSettingsRedirect(location)} />} />
            {/* /ai is a legacy surface — Equanaut Prime at /prime is the canonical account-level orchestrator. */}
            <Route exact path={aiRootPath} render={() => <Redirect to={primePath} />} />
            <Route
              exact
              path={organizationEquabotzAliasPath}
              render={({ match, location }) => (
                isLoggedIn
                  ? <Redirect to={equabotzSettingsRedirect(location)} />
                  : <Redirect to={`${Paths.userLogin}?from=${match.url}`} />
              )}
            />
            <Route
              exact
              path={organizationEquabotzPath}
              render={({ match, location }) => (
                isLoggedIn
                  ? <Redirect to={equabotzSettingsRedirect(location, match.params.organization)} />
                  : <Redirect to={`${Paths.userLogin}?from=${match.url}`} />
              )}
            />
            {/* Equabotz section — full Equa account + AI configuration (canonical) */}
            <Route path={equabotzPath}>
              <Switch>
                <Route
                  exact
                  path={equabotzPath}
                  render={({ location }) => (
                    <Redirect to={equabotzSettingsRedirect(location)} />
                  )}
                />
                {equabotzRoutes.map(mapRoutes)}
              </Switch>
            </Route>

            {/* Fleet Tracker (spec 022) — read-only observer dashboard iframe */}
            <Route path={fleetTrackerPath}>
              <Switch>{fleetTrackerRoutes.map(mapRoutes)}</Switch>
            </Route>

            {/* AI section — only non-root /ai/* paths reach here; /ai redirects to /prime above */}
            <Route path={aiRootPath}>
              <Switch>{aiRoutes.map(mapRoutes)}</Switch>
            </Route>

            {/* Marketing section (public) */}
            <Route
              exact
              path={[
                aboutPath,
                featuresPath,
                pricingPath,
                contactPath,
                privacyPath,
                termsPath,
                blogPostPath,
                blogPath,
                caseStudiesPath,
                integrationsPath,
                securityPath,
                changelogPath,
              ]}
            >
              <Switch>{marketingRoutes.map(mapRoutes)}</Switch>
            </Route>


            {/* Spec 053: /profile/billing exact redirects MUST sit above the
                non-exact <Route path={profilePath}> below, otherwise React
                Router's Switch picks the wider /profile/* match first and
                profileRoutes (which has no /billing entry) renders a blank
                app shell. */}
            <Route exact path="/profile/billing">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            <Route exact path="/profile/settings/billing">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            <Route path={profilePath}>
              <Switch>
                <Route exact path={profilePath} render={() => <Redirect to={dashboardPath} />} />
                {profileRoutes.map(mapRoutes)}
              </Switch>
            </Route>
            <Route exact path={guestOrganizationsPath}>
              <SubHeader title={<SubTitle hideLogo name={'My Organizations'} />} withMargin={true} />
              <Switch>{guestOrganizationsRoutes.map(mapRoutes)}</Switch>
            </Route>
            <Route path={guestProfilePath}>
              <Switch>{guestRoutes.map(mapRoutes)}</Switch>
            </Route>
            <Route exact path={Paths.organizationList}>
              <SubHeader title={<SubTitle hideLogo name={'My Organizations'} />} withMargin={true} />
              <Switch>{organizationListRoutes.map(mapRoutes)}</Switch>
            </Route>
            {/* FIX-QA059-01/03: Common short-path aliases that historically returned bare "Not Found".
                /messaging is org-scoped so redirect to org list for org selection.
                QA-058 P1-VIS-001 / QA-059 P1-EW-001, P1-EW-003. */}
            <Route exact path="/dashboard">
              <Redirect to={isLoggedIn ? dashboardPath : Paths.userLogin} />
            </Route>
            <Route exact path="/settings">
              <Redirect to={isLoggedIn ? dashboardPath : Paths.userLogin} />
            </Route>
            <Route exact path="/app">
              <Redirect to={organizationListPath} />
            </Route>
            <Route exact path="/messaging">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            {/* Spec 052: /settings/billing 500 fix. Without these guards the
                trailing organizationRoutes catch-all binds the literal
                string "settings" to the :org param and emits a 500 from
                /api/v1/organization/settings. Likewise /billing alone has
                no top-level route and falls through to marketing 404. */}
            <Route exact path="/settings/billing">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            <Route exact path="/billing">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            {/* Spec 052: orphan-route shims for common short paths that
                otherwise render the marketing 404 page even for logged-in
                users. The trailing catch-all below still handles every
                other URL the same way it did before. */}
            {/* Spec 053: bare /dataroom and /hh-finance are org-scoped
                in the canonical routes (/organization/:org/dataroom and
                /organization/:org/hh-finance). The bare paths fall through
                to the trailing catch-all and render the marketing 404.
                Redirect to the org list so the user can pick an org and
                land on the correct org-scoped URL. */}
            <Route exact path="/dataroom">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            <Route exact path="/hh-finance">
              <Redirect to={isLoggedIn ? organizationListPath : Paths.userLogin} />
            </Route>
            <Route exact path="/help">
              <Redirect to={isLoggedIn ? dashboardPath : Paths.userLogin} />
            </Route>
            <Route exact path="/support">
              <Redirect to={isLoggedIn ? dashboardPath : Paths.userLogin} />
            </Route>
            <Route>
              <Switch>{organizationRoutes.concat(taskThreadRoutes, routes).map(mapRoutes)}</Switch>
            </Route>
          </Switch>
        </ChunkErrorBoundary>
      </StyledRoutes>
    </StyledRoutesContainer>
  )
}

const mapStateToProps: StateTransform<RoutesProps> = s => ({ user: s.user })

export const AppRoutes = connect(mapStateToProps)(Routes)
